import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Api, netflixToLibraryItem, curiosityToLibraryItem } from "../lib/api.js";
import { getActiveProfileId, setActiveProfileId } from "../lib/profile.js";
import { useDismissals } from "../lib/useDismissals.js";
import { MediaStrip } from "../components/MediaStrip.js";
import { MagicSpinner } from "../components/MagicSpinner.js";
import { AnimeStrip, type AnimePick } from "../components/AnimeStrip.js";
import { HighlyRecommendedStrip, type RecommendedItem } from "../components/HighlyRecommendedStrip.js";
import { MoodFinder } from "../components/MoodFinder.js";
import { DismissButton } from "../components/DismissButton.js";
import type { DismissalItem, LibraryItem } from "../lib/api.js";
import { applyHomeLayout, DEFAULT_CAROUSEL, HOME_SERVICE_KINDS } from "../lib/homeLayout.js";

interface SectionItem {
  id: string;
  title: string;
  thumbnail_url: string | null;
  kind: string;
  category?: string | null;
  creator_name?: string | null;
  tmdb_backdrop_url?: string | null;
  tmdb_rating?: number | null;
  tmdb_genres?: string[] | null;
  tmdb_overview?: string | null;
}

interface Section {
  kind: string;
  label: string;
  items: SectionItem[];
}

interface MarqueePick {
  id: string;
  kind: string;
  title: string;
  episode_title: string | null;
  backdrop_url: string | null;
  thumbnail_url: string | null;
  tmdb_rating: number | null;
  tmdb_genres: string[] | null;
  tmdb_overview: string | null;
  creator_name: string | null;
  link: string;
}

interface Marquee {
  movie: MarqueePick | null;
  tv: MarqueePick | null;
  youtube: MarqueePick | null;
}

export function Home(): JSX.Element {
  const profilesQ = useQuery({ queryKey: ["profiles"], queryFn: Api.profiles.list });
  const activeId = getActiveProfileId();
  useEffect(() => {
    if (!activeId && profilesQ.data?.profiles[0]) {
      setActiveProfileId(profilesQ.data.profiles[0].id);
    }
  }, [profilesQ.data, activeId]);
  const profileId = activeId ?? profilesQ.data?.profiles[0]?.id ?? null;
  const activeProfile = profilesQ.data?.profiles.find((p) => p.id === profileId) ?? null;
  const { dismissedIds, dismiss, dismissing } = useDismissals(profileId);

  const recsQ = useQuery({
    queryKey: ["recs", profileId],
    queryFn: () => Api.recommendations(profileId!),
    enabled: !!profileId,
    refetchInterval: 5 * 60 * 1000,
  });

  const health = useQuery({
    queryKey: ["health"],
    queryFn: Api.health,
    refetchInterval: 30_000,
  });


  const rawSectionsForDiscover = ((recsQ.data?.sections ?? []) as Section[])
    .map((s) => ({ ...s, items: s.items.filter((item) => !dismissedIds.has(item.id)) }))
    .filter((s) => s.items.length > 0);
  const discoverQ = useQuery({
    queryKey: ["discover"],
    queryFn: Api.discover,
    enabled: !!profileId && !recsQ.isLoading && rawSectionsForDiscover.length === 0,
    staleTime: 30 * 60 * 1000,
  });

  // Trending on Netflix — catalog discovery; titles play via Real-Debrid.
  const netflixTrendingQ = useQuery({
    queryKey: ["netflix-trending"],
    queryFn: Api.library.netflixTrending,
    enabled: !!profileId,
    staleTime: 6 * 60 * 60 * 1000,
  });

  // Featured on CuriosityStream — Douglas's subscription; plays their HLS directly.
  const curiosityFeaturedQ = useQuery({
    queryKey: ["curiosity-featured"],
    queryFn: Api.library.curiosityFeatured,
    enabled: !!profileId,
    staleTime: 6 * 60 * 60 * 1000,
  });

  // Per-carousel displayed items — stored server-side per profile so all
  // devices (phone, PC, tablet) see the same stable carousels.
  const [displayed, setDisplayed] = useState<Record<string, SectionItem[]>>({});
  const [loadedMore, setLoadedMore] = useState<Record<string, number>>({});
  const lastRefreshAt = useRef<Record<string, number>>({});
  const serverStateLoaded = useRef(false);
  const savePending = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load saved state from server on first mount
  useEffect(() => {
    if (!profileId || serverStateLoaded.current) return;
    serverStateLoaded.current = true;
    Api.profiles.getCarouselState(profileId).then(({ state }) => {
      if (Object.keys(state).length === 0) return;
      // Restore timestamps
      for (const [kind, s] of Object.entries(state)) {
        lastRefreshAt.current[kind] = s.lastRefreshAt;
      }
      // Restore displayed items — will be reconciled with fresh server data in seed effect
      setDisplayed(prev => {
        const next = { ...prev };
        for (const [kind, s] of Object.entries(state)) {
          if (!next[kind] && s.itemIds.length > 0) {
            // Placeholder — seed effect will fill in actual items matching these IDs
            next[`__saved_${kind}`] = s.itemIds as unknown as SectionItem[];
          }
        }
        return next;
      });
    }).catch(() => { /* first load or offline — start fresh */ });
  }, [profileId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced save to server after any carousel change
  const saveCarouselState = useCallback((newDisplayed: Record<string, SectionItem[]>) => {
    if (!profileId) return;
    if (savePending.current) clearTimeout(savePending.current);
    savePending.current = setTimeout(() => {
      const snapshot: Record<string, { itemIds: string[]; lastRefreshAt: number }> = {};
      for (const [kind, items] of Object.entries(newDisplayed)) {
        if (kind.startsWith('__saved_')) continue;
        snapshot[kind] = {
          itemIds: (items as SectionItem[]).map(i => i.id),
          lastRefreshAt: lastRefreshAt.current[kind] ?? Date.now(),
        };
      }
      Api.profiles.putCarouselState(profileId, snapshot).catch(() => undefined);
    }, 3000);
  }, [profileId]);

  const layoutQ = useQuery({
    queryKey: ["home-layout", profileId],
    queryFn: () => Api.profiles.getHomeLayout(profileId!),
    enabled: !!profileId,
    staleTime: 60_000,
  });
  const DEFAULT_CS = DEFAULT_CAROUSEL;
  const rawCS = layoutQ.data?.layout.carouselSettings;
  // Stable reference — only changes when layout actually changes
  const carouselSettings = useMemo(() => rawCS ?? {}, [rawCS]);
  const csFor = useCallback((kind: string) => carouselSettings[kind] ?? DEFAULT_CS, [carouselSettings]);

  // Save to server whenever displayed changes
  useEffect(() => {
    if (Object.keys(displayed).length > 0) saveCarouselState(displayed);
  }, [displayed, saveCarouselState]);

  // Keep a ref so the rotation timer can read the latest csFor without being
  // in its dependency array (prevents the interval from resetting constantly).
  const csForRef = useRef(csFor);
  useEffect(() => { csForRef.current = csFor; }, [csFor]);

  // Seed carousel display cache when recs arrive.
  // If saved IDs exist from server, restore those items (same order, same picks).
  // Only falls back to fresh slice if no saved state.
  useEffect(() => {
    const sections = (recsQ.data?.sections ?? []) as Section[];
    setDisplayed(prev => {
      const next = { ...prev };
      for (const s of sections) {
        const savedKey = `__saved_${s.kind}`;
        const savedIds = next[savedKey] as unknown as string[] | undefined;
        if (savedIds && !next[s.kind]) {
          // Restore the saved order from the server pool
          const byId = new Map(s.items.map(i => [i.id, i]));
          const restored = savedIds.map(id => byId.get(id)).filter(Boolean) as SectionItem[];
          if (restored.length > 0) {
            next[s.kind] = restored;
            delete next[savedKey];
            continue;
          }
          delete next[savedKey];
        }
        if (!next[s.kind]) {
          // No saved state — seed fresh
          const { count } = csForRef.current(s.kind);
          next[s.kind] = s.items.slice(0, count);
          lastRefreshAt.current[s.kind] = Date.now();
        }
      }
      return next;
    });
  }, [recsQ.data]);

  // Rotation timer — stable 60 s interval; resets only when recs data changes.
  // Reads csFor from a ref so csFor changes don't tear down the interval.
  useEffect(() => {
    const sections = (recsQ.data?.sections ?? []) as Section[];
    // Stamp all known sections NOW so the first interval fire doesn't see 0.
    for (const s of sections) {
      if (!lastRefreshAt.current[s.kind]) {
        lastRefreshAt.current[s.kind] = Date.now();
      }
    }
    const t = window.setInterval(() => {
      const secs = (recsQ.data?.sections ?? []) as Section[];
      if (secs.length === 0) return;
      setDisplayed(prev => {
        let changed = false;
        const next = { ...prev };
        for (const s of secs) {
          const { count, refreshPct, refreshMinutes } = csForRef.current(s.kind);
          const lastMs = lastRefreshAt.current[s.kind] ?? Date.now();
          if (Date.now() - lastMs < refreshMinutes * 60_000) continue;
          const current = next[s.kind] ?? s.items.slice(0, count);
          const replaceN = Math.max(1, Math.round(current.length * refreshPct / 100));
          const currentIds = new Set(current.map(i => i.id));
          const pool = s.items.filter(i => !currentIds.has(i.id));
          if (pool.length === 0) { lastRefreshAt.current[s.kind] = Date.now(); continue; }
          const incoming = pool.slice(0, replaceN);
          next[s.kind] = [...incoming, ...current.slice(0, current.length - replaceN)];
          lastRefreshAt.current[s.kind] = Date.now();
          changed = true;
        }
        return changed ? next : prev;
      });
    }, 60_000);
    return () => window.clearInterval(t);
  }, [recsQ.data]);

  // Force-refresh a single carousel
  const forceRefresh = useCallback((kind: string, allItems: SectionItem[]) => {
    const { count } = csForRef.current(kind);
    const shuffled = [...allItems].sort(() => Math.random() - 0.5);
    setDisplayed(prev => ({ ...prev, [kind]: shuffled.slice(0, count) }));
    setLoadedMore(prev => ({ ...prev, [kind]: 0 }));
    lastRefreshAt.current[kind] = Date.now();
  }, []);

  if (!profileId) {
    // Profiles loaded but none exist → auto-navigate to creation.
    if (profilesQ.data) return <Navigate to="/profiles" replace />;
    // Still loading — show skeleton.
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="skeleton w-48 h-4 rounded" />
      </div>
    );
  }

  const rawSections = rawSectionsForDiscover;
  const rawMarquee = ((recsQ.data as { marquee?: Marquee } | undefined)?.marquee ?? null) as Marquee | null;
  const visiblePick = (pick: MarqueePick | null): MarqueePick | null =>
    pick && !dismissedIds.has(pick.id) ? pick : null;
  const marquee: Marquee | null = rawMarquee ? {
    movie: visiblePick(rawMarquee.movie),
    tv: visiblePick(rawMarquee.tv),
    youtube: visiblePick(rawMarquee.youtube),
  } : null;
  const marqueeIds = new Set<string>(
    [marquee?.movie?.id, marquee?.tv?.id, marquee?.youtube?.id].filter(Boolean) as string[],
  );

  const layout = layoutQ.data?.layout;
  const sections = rawSections;
  const netflixItems = (netflixTrendingQ.data?.items ?? [])
    .map(netflixToLibraryItem)
    .filter((item) => !dismissedIds.has(item.id));
  const curiosityItems = (curiosityFeaturedQ.data?.items ?? [])
    .map(curiosityToLibraryItem)
    .filter((item) => !dismissedIds.has(item.id));

  const recommendationRows: { kind: string; node: React.ReactNode }[] = sections.map((s) => {
    const { count } = csFor(s.kind);
    const extraPages = loadedMore[s.kind] ?? 0;
    const totalCount = count + extraPages * count;

    // Preserve stable/rotated picks, but always enforce the latest card count.
    // Fill from the current pool when the count increases or dismissed cards vanish.
    const cachedItems = displayed[s.kind] ?? s.items;
    const cachedIds = new Set(cachedItems.map((item) => item.id));
    const displayItems = [
      ...cachedItems,
      ...s.items.filter((item) => !cachedIds.has(item.id)),
    ].slice(0, count);
    const allPoolItems = s.items;
    const visibleItems = (extraPages > 0
      ? allPoolItems.slice(0, totalCount)
      : displayItems
    ).filter((item) => !dismissedIds.has(item.id));

    if (s.kind === "anime_trending" || s.kind === "anime_top" || s.kind === "anime_season") {
      return {
        kind: s.kind,
        node: (
          <AnimeStrip
            label={s.label}
            caption={`${visibleItems.length} ${visibleItems.length === 1 ? "title" : "titles"} · launches Crunchyroll`}
            items={visibleItems as unknown as AnimePick[]}
            onRefresh={() => forceRefresh(s.kind, s.items)}
            onDismiss={dismiss}
            dismissing={dismissing}
          />
        ),
      };
    }
    if (s.kind === "highly_recommended") {
      return {
        kind: s.kind,
        node: (
          <HighlyRecommendedStrip
            label={s.label}
            items={visibleItems as unknown as RecommendedItem[]}
            profileId={profileId}
          />
        ),
      };
    }
    const filtered = visibleItems.filter((it) => !marqueeIds.has(it.id));
    if (filtered.length === 0) return { kind: s.kind, node: null };
    const canLoadMore = count >= 20 && allPoolItems.length > totalCount;
    return {
      kind: s.kind,
      node: (
        <div>
          <MediaStrip
            label={s.label}
            items={filtered as unknown as LibraryItem[]}
            variant={count <= 2 ? "wide" : variantFor(s.kind)}
            caption={captionFor(s.kind, filtered.length)}
            onRefresh={() => forceRefresh(s.kind, s.items)}
            episodeLevel={s.kind === "continue"}
            onDismiss={dismiss}
            dismissing={dismissing}
          />
          {canLoadMore && (
            <button
              onClick={() => setLoadedMore(prev => ({ ...prev, [s.kind]: (prev[s.kind] ?? 0) + 1 }))}
              className="pill-ghost mx-auto -mt-6 mb-10 flex"
            >
              Load {count} more
            </button>
          )}
        </div>
      ),
    };
  });

  const serviceRows: { kind: string; node: React.ReactNode }[] = [
    {
      kind: "netflix_trending",
      node: netflixItems.length > 0 && (
        <MediaStrip
          label="Trending on Netflix"
          items={netflixItems.slice(0, csFor("netflix_trending").count)}
          variant="poster"
          caption="trending on Netflix · stream via Real-Debrid"
          onDismiss={dismiss}
          dismissing={dismissing}
        />
      ),
    },
    {
      kind: "curiosity_featured",
      node: curiosityItems.length > 0 && (
        <MediaStrip
          label="Featured on CuriosityStream"
          href="/service/curiosity"
          items={curiosityItems.slice(0, csFor("curiosity_featured").count)}
          variant="poster"
          caption="featured documentaries · plays with your CuriosityStream account"
          onDismiss={dismiss}
          dismissing={dismissing}
        />
      ),
    },
    ...(["apple", "hbo", "cable", "nfb"] as const).map((provider) => ({
      kind: `provider_${provider}`,
      node: (
        <ProviderStrip
          provider={provider}
          count={csFor(`provider_${provider}`).count}
          dismissedIds={dismissedIds}
          onDismiss={dismiss}
          dismissing={dismissing}
        />
      ),
    })),
  ];

  // Older saved layouts predate service-row controls. Keep those rows in their
  // original position until the next Preferences save writes a unified order.
  const unifiedServiceLayout = layout?.order.some((kind) => HOME_SERVICE_KINDS.includes(kind)) ?? false;
  const homeRows = unifiedServiceLayout
    ? applyHomeLayout([...serviceRows, ...recommendationRows], layout)
    : [
        ...serviceRows.filter((row) => !layout?.hidden.includes(row.kind)),
        ...applyHomeLayout(recommendationRows, layout),
      ];

  return (
    <div>
      <Marquee
        profileName={activeProfile?.display_name ?? "—"}
        profileId={profileId}
        mark={activeProfile?.forecourt_mark ?? activeProfile?.avatar_emoji ?? "★"}
        health={health.data?.ok ?? false}
        marquee={marquee}
        onDismiss={dismiss}
        dismissing={dismissing}
      />

      {/* The AI spinner goes first — it is the point of the page. */}
      {profileId && <MagicSpinner profileId={profileId} />}
      {recsQ.isLoading && (
        <div className="space-y-10">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i}>
              <div className="skeleton h-3 w-32 mb-3" />
              <div className="flex gap-4 overflow-hidden">
                {Array.from({ length: 6 }).map((_, j) => <div key={j} className="skeleton w-[220px] aspect-[16/11]" />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {!recsQ.isLoading && sections.length === 0 && !discoverQ.isLoading && (discoverQ.data?.sections ?? []).length === 0 && (
        <div className="card p-12 text-parchment text-center font-display tracking-smallcaps">
          Empty. Add sources, finish onboarding, then come back.
        </div>
      )}
      {!recsQ.isLoading && sections.length === 0 && (discoverQ.data?.sections ?? []).map((s) => {
        const items = (s.items as unknown as LibraryItem[]).filter((item) => !dismissedIds.has(item.id));
        if (items.length === 0) return null;
        return (
          <MediaStrip
            key={s.kind}
            label={s.label}
            items={items}
            variant="poster"
            caption={`${items.length} title${items.length !== 1 ? "s" : ""} · stream via Real-Debrid`}
            onDismiss={dismiss}
            dismissing={dismissing}
          />
        );
      })}

      {homeRows.map((row) => (
        <React.Fragment key={row.kind}>{row.node}</React.Fragment>
      ))}
    </div>
  );
}

/**
 * One "Hot on <service>" strip. Kept as its own component so each provider's
 * query caches and refetches independently — a slow TMDB round on one strip
 * never holds back the others.
 */
function ProviderStrip({ provider, count, dismissedIds, onDismiss, dismissing }: {
  provider: "apple" | "hbo" | "cable" | "nfb";
  count: number;
  dismissedIds: Set<string>;
  onDismiss: (item: DismissalItem) => void;
  dismissing: boolean;
}): JSX.Element | null {
  const q = useQuery({
    queryKey: ["provider-trending", provider],
    queryFn: () => Api.library.providerTrending(provider),
    staleTime: 3 * 60 * 60_000,
  });
  const items = (q.data?.items ?? [])
    .map(netflixToLibraryItem)
    .filter((item) => !dismissedIds.has(item.id))
    .slice(0, count);
  if (items.length === 0) return null;
  const label = q.data?.label ?? provider;
  return (
    <MediaStrip
      label={`Hot on ${label}`}
      href={`/service/${provider}`}
      items={items}
      variant="poster"
      caption={`hot on ${label} · stream via Real-Debrid`}
      onDismiss={onDismiss}
      dismissing={dismissing}
    />
  );
}

function CarouselWrapper({ children, label: _label, onRefresh, refreshMinutes, lastRefreshAt }: {
  children: React.ReactNode;
  label: string;
  onRefresh: () => void;
  refreshMinutes: number;
  lastRefreshAt: number | undefined;
}): JSX.Element {
  const nextRefreshMin = lastRefreshAt
    ? Math.max(0, Math.round((lastRefreshAt + refreshMinutes * 60_000 - Date.now()) / 60_000))
    : null;
  return (
    <div className="relative group">
      <button
        onClick={onRefresh}
        className="absolute top-0 right-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity pill-ghost text-parchment/60 hover:text-gold"
        title={nextRefreshMin !== null ? `Auto-refresh in ${nextRefreshMin}m · click to refresh now` : "Refresh"}
      >
        <RefreshCw size={13} />
      </button>
      {children}
    </div>
  );
}

function applyLayout(
  sections: Section[],
  layout: { order: string[]; hidden: string[] } | undefined,
): Section[] {
  if (!layout) return sections;
  const hidden = new Set(layout.hidden);
  const visible = sections.filter((s) => !hidden.has(s.kind));
  if (layout.order.length === 0) return visible;
  const byKind = new Map(visible.map((s) => [s.kind, s]));
  const used = new Set<string>();
  const out: Section[] = [];
  for (const kind of layout.order) {
    const s = byKind.get(kind);
    if (s) { out.push(s); used.add(kind); }
  }
  // Anything the user hasn't explicitly ordered (e.g. a new section kind)
  // appends after their custom order.
  for (const s of visible) if (!used.has(s.kind)) out.push(s);
  return out;
}

function variantFor(kind: string): "default" | "wide" | "poster" {
  if (kind.startsWith("genre_")) return "poster";
  switch (kind) {
    case "pinned":
    case "continue":
    case "highly_recommended":
      return "wide";
    case "because_loved":
    case "acclaimed_in_genre":
    case "hidden_gems":
    case "vault":
    case "quick_picks":
    case "movies":
    case "tv_series":
      return "poster";
    case "youtube":
    case "new_arrivals":
    case "from_creators":
      return "default";
    default:
      return "default";
  }
}

function captionFor(_kind: string, n: number): string {
  return `${n} ${n === 1 ? "title" : "titles"}`;
}

function Marquee({
  profileName,
  profileId,
  marquee,
  onDismiss,
  dismissing,
}: {
  profileName: string;
  profileId: string;
  mark: string;
  health: boolean;
  marquee: Marquee | null;
  onDismiss: (item: DismissalItem) => void;
  dismissing: boolean;
}): JSX.Element {
  const slots: { key: "movie" | "tv" | "youtube"; label: string; pick: MarqueePick | null }[] = [
    { key: "movie",   label: "Film",    pick: marquee?.movie ?? null },
    { key: "tv",      label: "Series",  pick: marquee?.tv ?? null },
    { key: "youtube", label: "Watch",   pick: marquee?.youtube ?? null },
  ];
  const populated = slots.filter((s) => s.pick !== null);
  return (
    <header className="mb-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 px-1">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="section-label text-gold">FOR YOU TONIGHT</span>
          <span className="text-parchment/50 text-xs">curated for {profileName}</span>
        </div>
        <MoodFinder profileId={profileId} />
      </div>
      {populated.length === 0 ? (
        <div className="card p-8 text-parchment text-center font-display tracking-smallcaps">
          The marquee is dim — finish onboarding to light it up.
        </div>
      ) : (
        <div className={`grid gap-3 ${
          populated.length === 3 ? "grid-cols-1 sm:grid-cols-3" :
          populated.length === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"
        }`}>
          {slots.map((s) => s.pick && (
            <MarqueeCard
              key={s.key}
              label={s.label}
              pick={s.pick}
              onDismiss={onDismiss}
              dismissing={dismissing}
            />
          ))}
        </div>
      )}
    </header>
  );
}

function MarqueeCard({ label, pick, onDismiss, dismissing }: {
  label: string;
  pick: MarqueePick;
  onDismiss: (item: DismissalItem) => void;
  dismissing: boolean;
}): JSX.Element {
  const backdrop = pick.backdrop_url ?? pick.thumbnail_url ?? null;
  const rating = typeof pick.tmdb_rating === "number"
    ? Math.round(pick.tmdb_rating * 10) / 10
    : null;
  const genres = (pick.tmdb_genres ?? []).slice(0, 3);
  return (
    <div className="relative group">
      <Link
        to={pick.link}
        className="relative rounded-2xl border border-hairline overflow-hidden block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goldHi group"
      >
      <div className="relative aspect-[2/1] bg-lacquer">
        {backdrop && (
          <img
            src={backdrop}
            alt=""
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            style={{ filter: "saturate(0.9) contrast(1.05)" }}
            referrerPolicy="no-referrer"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
        )}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(to top, rgba(10,4,4,0.97) 0%, rgba(10,4,4,0.5) 45%, rgba(10,4,4,0.05) 100%)",
          }}
        />
        {/* Label + rating top row */}
        <div className="absolute top-3 left-3 right-12 flex items-center justify-between">
          <span className="px-2 py-0.5 rounded bg-ink/75 border border-gold/30 text-goldHi text-[9px] uppercase tracking-widest font-display">
            {label}
          </span>
          {rating !== null && (
            <span className="px-2 py-0.5 rounded bg-ink/75 text-goldHi text-[10px] font-display tabular-nums">
              ★ {rating}
            </span>
          )}
        </div>
        {/* Content at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="font-display text-ivory text-base leading-snug drop-shadow-lg line-clamp-1">
            {pick.title}
          </h3>
          {genres.length > 0 && (
            <p className="text-parchment/60 text-[11px] mt-1 uppercase tracking-widest">
              {genres.join(" · ")}
            </p>
          )}
          {pick.tmdb_overview && (
            <p className="text-parchment/50 text-xs mt-2 line-clamp-2 leading-relaxed">
              {pick.tmdb_overview}
            </p>
          )}
        </div>
      </div>
      </Link>
      <DismissButton itemTitle={pick.title} onDismiss={() => onDismiss(pick)} disabled={dismissing} />
    </div>
  );
}
